import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import {
  approveBudgetCenter,
  copyFromMonth,
  discardBudgetCenterDraft,
  fetchAvailableMonths,
  fetchBudgetCenter,
  fetchBudgetComments,
  fetchBudgetCenterScopeSpend,
  markBudgetCenterLive,
  postBudgetComment,
  saveBudgetCenterDraft,
} from '@src/api/actions/dashboard/clientBudgets';
import Page from '@src/components/layout/Page';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useLocalStorage from '@src/hooks/useLocalStorage';
import useMultiClientBudgetCenter from '@src/hooks/useMultiClientBudgetCenter';
import { useClientView } from '@src/components/layout/AppLayout/ClientViewSwitcher';

function formatCurrency(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '$0.00';
  }
  return number.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatNumberInput(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const raw = String(value).replace(/,/g, '');
  if (!/^[-]?\d*\.?\d*$/.test(raw)) {
    return String(value);
  }
  const [whole, fraction] = raw.split('.');
  const normalizedWhole = whole === '' || whole === '-' ? whole : Number(whole).toLocaleString('en-US');
  if (fraction !== undefined) {
    return `${normalizedWhole}.${fraction}`;
  }
  return normalizedWhole;
}

function getErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) {
    return fallback;
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data.detail) {
    return data.detail;
  }
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join(', ');
}

function getCurrentMonthStart() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10);
}

function getTodayDateString() {
  return dayjs().format('YYYY-MM-DD');
}

function formatMonthLabel(value) {
  if (!value) {
    return 'Current Month';
  }
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatActorLabel(event) {
  if (!event) {
    return '';
  }
  return event?.actor?.name || event?.actor?.email || 'Unknown user';
}

function formatHistoryValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Empty';
  }
  if (typeof value === 'object') {
    if (value.name) {
      return value.name;
    }
    if (Array.isArray(value)) {
      return value.join(', ') || 'Empty';
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function formatStatusLabel(value, options = {}) {
  const { planned = false } = options;
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'off') {
    return 'Off';
  }
  if (normalized === 'pending_turn_off') {
    return 'Pending Turn Off';
  }
  if (normalized === 'active') {
    return 'Live';
  }
  if (normalized === 'approved') {
    return 'Approved';
  }
  if (normalized === 'pending_approval') {
    return 'Submitted';
  }
  if (normalized === 'draft') {
    return planned ? 'Planned' : 'Draft';
  }
  if (normalized === 'archived') {
    return 'Archived';
  }
  return normalized ? normalized.replaceAll('_', ' ') : 'Draft';
}

function getStatusChipColor(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'off') {
    return 'error';
  }
  if (normalized === 'pending_turn_off') {
    return 'warning';
  }
  if (normalized === 'active') {
    return 'success';
  }
  if (normalized === 'approved' || normalized === 'pending_approval') {
    return 'warning';
  }
  if (normalized === 'archived') {
    return 'default';
  }
  return 'info';
}

function getPacingColor(pct) {
  if (pct < 90) return '#3b82f6';
  if (pct < 110) return '#22c55e';
  if (pct < 120) return '#f59e0b';
  return '#ef4444';
}

function getPacingPillSx(pct) {
  if (pct < 90) return { color: 'info.dark', bgcolor: 'rgba(59,130,246,0.15)', borderColor: 'rgba(59,130,246,0.45)' };
  if (pct < 110) return { color: 'success.dark', bgcolor: 'rgba(34,197,94,0.15)', borderColor: 'rgba(34,197,94,0.5)' };
  if (pct < 120) return { color: 'warning.dark', bgcolor: 'rgba(245,158,11,0.2)', borderColor: 'rgba(245,158,11,0.5)' };
  return { color: 'error.dark', bgcolor: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.45)' };
}

function PacingCell({ effectiveAmount, monthToDateSpend, pacingPercentage }) {
  const budget = Number(effectiveAmount || 0);
  const spend = Number(monthToDateSpend || 0);
  if (budget <= 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const backendPct = pacingPercentage === null || pacingPercentage === undefined || pacingPercentage === ''
    ? null
    : Number(pacingPercentage);
  const pct = backendPct !== null && Number.isFinite(backendPct)
    ? Math.round(backendPct)
    : Math.round((spend / budget) * 100);
  const barPct = Math.min(pct, 200);
  const barColor = getPacingColor(pct);
  const pillSx = getPacingPillSx(pct);

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: '0.8rem',
          px: 1,
          py: 0.25,
          borderRadius: 2,
          border: '1px solid',
          minWidth: 56,
          ...pillSx,
        }}
      >
        {pct}%
      </Box>
      <Box sx={{ flex: 1, minWidth: 40, height: 6, borderRadius: 1, bgcolor: 'grey.200', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(barPct, 100)}%`, height: '100%', borderRadius: 1, bgcolor: barColor, transition: 'width 300ms ease' }} />
      </Box>
    </Stack>
  );
}

function SummaryCard({ label, value, helper }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="overline">
            {label}
          </Typography>
          <Typography variant="h5">{value}</Typography>
          <Typography color="text.secondary" variant="body2">
            {helper}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SummaryMetric({ label, value, helper }) {
  return (
    <Stack minWidth={0} spacing={0.5}>
      <Typography color="text.secondary" variant="overline">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
      {helper ? (
        <Typography color="text.secondary" variant="caption">
          {helper}
        </Typography>
      ) : null}
    </Stack>
  );
}

function MonthNavigator({ value, onChange, isStaff }) {
  const parsed = dayjs(value || getCurrentMonthStart());
  const isThisMonth = parsed.isSame(dayjs(), 'month');
  const isNextMonth = parsed.isSame(dayjs().add(1, 'month'), 'month');
  const isPast = parsed.isBefore(dayjs(), 'month');

  const prev = () => onChange(parsed.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'));
  const next = () => onChange(parsed.add(1, 'month').startOf('month').format('YYYY-MM-DD'));

  return (
    <Stack alignItems="center" direction="row" spacing={0.5}>
      <Tooltip title="Previous month">
        <span>
          <IconButton onClick={prev} size="small">
            <ChevronLeftIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Box sx={{ minWidth: 148, textAlign: 'center' }}>
        <Typography fontWeight={600} variant="body1">
          {parsed.format('MMMM YYYY')}
        </Typography>
        {isThisMonth ? (
          <Chip color="success" label="This Month" size="small" variant="outlined" />
        ) : isNextMonth ? (
          <Chip color="info" label="Next Month" size="small" variant="outlined" />
        ) : isPast ? (
          <Chip label="Past" size="small" variant="outlined" />
        ) : null}
      </Box>
      <IconButton onClick={next} size="small">
        <ChevronRightIcon />
      </IconButton>
    </Stack>
  );
}

const WORKFLOW_STEPS_CLIENT = ['Set Budget', 'Submitted', 'Approved', 'Live'];
const WORKFLOW_STEPS_STAFF = ['Draft', 'Submitted', 'Approved', 'Live'];

function getWorkflowActiveStep(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'active') return 3;
  if (s === 'approved') return 2;
  if (s === 'pending_approval') return 1;
  return 0;
}

function WorkflowStepper({ status, isStaff }) {
  const steps = isStaff ? WORKFLOW_STEPS_STAFF : WORKFLOW_STEPS_CLIENT;
  const activeStep = getWorkflowActiveStep(status);
  return (
    <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', pt: 0.5 }}>
      {steps.map((label) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

function emptySegment(period) {
  return {
    budget_id: null,
    source_budget_id: null,
    vertical: '',
    location: '',
    channel: '',
    amount: '',
    notes: '',
    adjustment_total: '0.00',
    effective_amount: '',
    workflow_status: 'draft',
    adjustments: [],
    vertical_data: null,
    location_data: null,
    channel_data: null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: getTodayDateString(),
      notes: '',
    },
  };
}

function normalizeSegment(segment, period) {
  return {
    budget_id: segment?.budget_id ?? segment?.id ?? null,
    source_budget_id: segment?.source_budget_id ?? segment?.id ?? null,
    vertical: segment?.vertical ?? segment?.vertical_data?.id ?? '',
    location: segment?.location ?? segment?.location_data?.id ?? '',
    channel: segment?.channel ?? segment?.channel_data?.id ?? '',
    amount: segment?.amount ?? '',
    notes: segment?.notes ?? '',
    adjustment_total: segment?.adjustment_total ?? '0.00',
    effective_amount: segment?.effective_amount ?? segment?.amount ?? '',
    location_ids: segment?.location_ids ?? (segment?.location ? [Number(segment.location)] : []),
    month_to_date_spend: segment?.month_to_date_spend ?? 0,
    projected_spend: segment?.projected_spend ?? 0,
    remaining_per_day: segment?.remaining_per_day ?? 0,
    pacing_percentage: segment?.pacing_percentage ?? null,
    workflow_status: segment?.workflow_status ?? 'draft',
    has_pending_turn_off: Boolean(segment?.has_pending_turn_off),
    is_turned_off: Boolean(segment?.is_turned_off),
    adjustments: Array.isArray(segment?.adjustments) ? segment.adjustments : [],
    vertical_data: segment?.vertical_data ?? null,
    location_data: segment?.location_data ?? null,
    channel_data: segment?.channel_data ?? null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: getTodayDateString(),
      notes: '',
    },
  };
}

function segmentSignature(segment) {
  return [
    segment.vertical || '',
    segment.location || '',
    segment.channel || '',
    String(segment.amount || ''),
    String(segment.notes || ''),
  ].join('|');
}

function cloneSegment(segment) {
  return {
    ...segment,
    budget_id: null,
    source_budget_id: null,
    workflow_status: 'draft',
    adjustment_total: '0.00',
    effective_amount: segment?.amount ?? '',
    month_to_date_spend: '0.00',
    projected_spend: '0.00',
    remaining_per_day: '0.00',
    pacing_percentage: null,
    adjustments: [],
    pendingAdjustment: {
      amount_delta: '',
      effective_date: getTodayDateString(),
      notes: '',
    },
  };
}

function segmentDraftComparable(segment) {
  return {
    budget_id: segment?.budget_id ?? null,
    source_budget_id: segment?.source_budget_id ?? null,
    vertical: segment?.vertical || '',
    location: segment?.location || '',
    channel: segment?.channel || '',
    amount: String(segment?.amount ?? ''),
    notes: segment?.notes || '',
    adjustments: Array.isArray(segment?.adjustments)
      ? segment.adjustments.map((adjustment) => ({
        id: adjustment?.id ?? null,
        amount_delta: String(adjustment?.amount_delta ?? ''),
        effective_date: adjustment?.effective_date || '',
        notes: adjustment?.notes || '',
      }))
      : [],
  };
}

function buildSegmentLabel(segment, options) {
  const verticalLabel = options.verticals.find((row) => String(row.value) === String(segment.vertical || ''))?.label;
  const locationLabel = options.locations.find((row) => String(row.value) === String(segment.location || ''))?.label;
  return [verticalLabel, locationLabel].filter(Boolean).join(' / ') || 'Segment';
}

function isExistingSegment(segment) {
  return Boolean(segment?.budget_id);
}

function isLiveOriginSegment(segment, liveBudgetIds) {
  const sourceBudgetId = segment?.source_budget_id;
  if (!sourceBudgetId) {
    return false;
  }
  return Array.isArray(liveBudgetIds) && liveBudgetIds.includes(sourceBudgetId);
}

function getSegmentOperationalStatus(segment) {
  // Pending turn-off: client requested but staff hasn't approved yet
  if (segment?.has_pending_turn_off) {
    return 'pending_turn_off';
  }

  // Approved turn-off: staff approved the turn-off request
  if (segment?.is_turned_off) {
    return 'off';
  }

  // If the amount hasn't been entered yet (new row being filled in), treat as draft — not "off"
  const rawAmount = segment?.effective_amount ?? segment?.amount;
  if (rawAmount === '' || rawAmount === null || rawAmount === undefined) {
    return segment?.workflow_status || 'draft';
  }

  const effectiveAmount = Number(rawAmount);
  const monthToDateSpend = Number(segment?.month_to_date_spend ?? 0);
  const floor = Math.max(0, monthToDateSpend);

  if (effectiveAmount <= 0 || Math.abs(effectiveAmount - floor) < 0.005) {
    return 'off';
  }

  return segment?.workflow_status || 'draft';
}

function getBudgetCenterRowStatus(segment, { isLiveMonth, liveBudgetIds }) {
  const operationalStatus = String(getSegmentOperationalStatus(segment) || '').toLowerCase();
  if (operationalStatus === 'off') {
    return 'off';
  }
  if (operationalStatus === 'pending_turn_off') {
    return 'pending_turn_off';
  }
  if (isLiveMonth && isLiveOriginSegment(segment, liveBudgetIds)) {
    return 'active';
  }
  return segment?.workflow_status || 'draft';
}

const WALKTHROUGH_STEPS_STAFF = [
  {
    ref: 'clientSelector',
    title: 'Select a Client',
    description: 'Pick the client whose budget you want to manage.',
  },
  {
    ref: 'monthNavigator',
    title: 'Navigate Months',
    description: 'Use the arrows to switch between months. Set future month budgets, or view and adjust the current live month.',
  },
  {
    ref: 'budgetTable',
    title: 'Budget Segments',
    description: 'Each row is a budget segment with vertical and location targeting. For live months you\'ll see MTD spend, pacing, and remaining budget. Use Add Adjustment for in-month changes.',
  },
  {
    ref: 'actionBar',
    title: 'Approve or Mark Live',
    description: 'Approve submitted budgets or mark approved budgets live. You can also save drafts and discard changes.',
  },
  {
    ref: 'discussion',
    title: 'Discussion',
    description: 'Leave comments for the client or internal notes about budget changes.',
  },
];

const WALKTHROUGH_STEPS_CLIENT = [
  {
    ref: 'monthNavigator',
    title: 'Navigate Months',
    description: 'Use the arrows to view past months or set your budget for the upcoming month.',
  },
  {
    ref: 'budgetTable',
    title: 'Your Budget Segments',
    description: 'Each row is a budget segment. Set your monthly amount per vertical and location. For the current month you\'ll see your spend, pacing, and remaining budget.',
  },
  {
    ref: 'actionBar',
    title: 'Submit for Review',
    description: 'When your budget looks good, submit it for Lead Gurus to review. You can also save a draft to come back later.',
  },
  {
    ref: 'discussion',
    title: 'Discussion',
    description: 'Ask questions or discuss budget changes with Lead Gurus here.',
  },
];

function MobileSegmentCard({
  segment, index, rowErrors, isLiveMonth, liveOrigin, showAdjustmentColumns,
  verticalOptions, locationOptions, updateSegment, onNotes, onDuplicate,
  onAdjustment, onTurnOff, onDelete, canDelete, rowStatus, isFutureMonth, optionSet,
  previousAmount, isReadOnly,
}) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Chip
              color={getStatusChipColor(rowStatus)}
              label={formatStatusLabel(rowStatus, { planned: isFutureMonth && String(rowStatus || '').toLowerCase() === 'draft' })}
              size="small"
              variant="filled"
            />
            {!segment.vertical && !segment.location ? (
              <Chip label="Flexible" size="small" variant="soft" />
            ) : null}
          </Stack>

          <Stack direction="row" spacing={1}>
            <TextField
              disabled={liveOrigin || isReadOnly}
              error={Boolean(rowErrors?.segment)}
              fullWidth
              select
              size="small"
              label="Vertical"
              value={segment.vertical}
              onChange={(e) => updateSegment(index, 'vertical', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {verticalOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <Autocomplete
              multiple
              size="small"
              disabled={liveOrigin || isReadOnly}
              options={locationOptions}
              getOptionLabel={(opt) => opt.label || ''}
              value={locationOptions.filter((o) => (segment.location_ids || (segment.location ? [Number(segment.location)] : [])).includes(o.value))}
              onChange={(_, val) => {
                const ids = (val || []).map((item) => item.value);
                updateSegment(index, 'location_ids', ids);
                updateSegment(index, 'location', ids[0] || '');
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => <TextField {...params} label="Location" placeholder={segment.location_ids?.length ? '' : 'All'} />}
            />
          </Stack>

          {rowErrors?.segment ? (
            <Typography color="error" variant="caption">{rowErrors.segment}</Typography>
          ) : null}

          <TextField
            disabled={liveOrigin || isReadOnly}
            error={Boolean(rowErrors?.amount)}
            fullWidth
            helperText={rowErrors?.amount || ''}
            size="small"
            label="Starting Budget"
            inputProps={{ inputMode: 'decimal' }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            value={formatNumberInput(segment.amount)}
            onChange={(e) => updateSegment(index, 'amount', e.target.value.replace(/,/g, ''))}
          />
          {(() => {
            if (previousAmount === null || previousAmount === 0) return null;
            const delta = Number(segment.amount || 0) - previousAmount;
            if (delta === 0) return (
              <Typography variant="caption" color="text.secondary">Same as last month</Typography>
            );
            return (
              <Typography variant="caption" sx={{ color: delta > 0 ? 'success.main' : 'error.main' }}>
                was {formatCurrency(previousAmount)} last month
              </Typography>
            );
          })()}

          {showAdjustmentColumns ? (
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Adjustments</Typography>
                <Typography variant="body2">{formatCurrency(segment.adjustment_total)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Effective</Typography>
                <Typography variant="body2">{formatCurrency(segment.effective_amount || segment.amount)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">MTD Spend</Typography>
                <Typography variant="body2">{formatCurrency(segment.month_to_date_spend)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Remaining</Typography>
                <Typography
                  variant="body2"
                  sx={(Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0)) < 0
                    ? { color: 'error.main', fontWeight: 600 }
                    : undefined}
                >
                  {formatCurrency(Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0))}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Remaining/Day</Typography>
                <Typography variant="body2">{formatCurrency(segment.remaining_per_day)}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Pacing</Typography>
                <PacingCell effectiveAmount={segment.effective_amount || segment.amount} monthToDateSpend={segment.month_to_date_spend} pacingPercentage={segment.pacing_percentage} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 100 }}>
                <Typography color="text.secondary" variant="caption">Projected</Typography>
                <Typography variant="body2">{formatCurrency(segment.projected_spend)}</Typography>
              </Box>
            </Stack>
          ) : null}

          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Button size="small" onClick={() => onNotes(index)}>
              {segment.notes ? 'Edit Notes' : 'Notes'}
            </Button>
            <Button size="small" onClick={() => onDuplicate(index)}>Duplicate</Button>
            {showAdjustmentColumns ? (
              <Button
                color="primary"
                size="small"
                startIcon={<EditOutlinedIcon />}
                variant="contained"
                onClick={() => onAdjustment(index)}
              >
                Adjustment
              </Button>
            ) : null}
            {isLiveMonth ? (
              <Button size="small" onClick={() => onTurnOff(segment)}>Turn Off</Button>
            ) : null}
            <IconButton
              size="small"
              onClick={() => onDelete(index)}
              disabled={!canDelete || liveOrigin}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function WalkthroughOverlay({ stepIndex, steps, stepRefs, onNext, onBack, onSkip }) {
  const step = steps[stepIndex];
  const ref = stepRefs[step?.ref];
  const el = ref?.current;
  const [rect, setRect] = useState(null);

  // Lock body scroll while tour is active
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Auto-skip steps whose target element doesn't exist
  useEffect(() => {
    if (step && !ref?.current) {
      const timer = setTimeout(() => onNext(), 50);
      return () => clearTimeout(timer);
    }
  }, [step, ref, onNext]);

  useEffect(() => {
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Wait for scroll to settle then measure
      const timer = setTimeout(() => {
        setRect(el.getBoundingClientRect());
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [el, stepIndex]);

  if (!step || !el || !rect) return null;

  const isLast = stepIndex === steps.length - 1;
  const tooltipHeight = 180; // approximate tooltip height
  const spaceBelow = window.innerHeight - rect.bottom - 16;
  const placeAbove = spaceBelow < tooltipHeight && rect.top > tooltipHeight;
  const tooltipTop = placeAbove ? rect.top - tooltipHeight - 16 : rect.bottom + 16;

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onSkip}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          zIndex: 1300,
          cursor: 'pointer',
        }}
      />
      {/* Highlight cutout */}
      <Box
        sx={{
          position: 'fixed',
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
          zIndex: 1301,
          pointerEvents: 'none',
        }}
      />
      {/* Tooltip card */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: tooltipTop,
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 380)),
          width: 360,
          zIndex: 1302,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>{step.title}</Typography>
            <Typography color="text.secondary" variant="caption">
              {stepIndex + 1} / {steps.length}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">{step.description}</Typography>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button size="small" variant="text" onClick={onSkip}>Skip tour</Button>
            <Stack direction="row" spacing={1}>
              {stepIndex > 0 ? (
                <Button size="small" variant="text" onClick={onBack}>Back</Button>
              ) : null}
              <Button size="small" variant="contained" onClick={onNext}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

export default function BudgetCenterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const realUser = state?.user;
  const { viewAsClient } = useClientView();

  // Staff preview mode: simulate a single-scope client member
  const isPreviewMode = Boolean(viewAsClient) && Boolean(realUser?.is_staff || realUser?.is_superuser);
  const user = isPreviewMode
    ? {
        ...realUser,
        is_staff: false,
        is_superuser: false,
        client_memberships: [
          {
            client_id: viewAsClient.id,
            client_name: viewAsClient.name,
            client_slug: viewAsClient.slug || viewAsClient.name,
            role: 'admin',
            budget_center_enabled: true,
          },
        ],
      }
    : realUser;
  const memberships = user?.client_memberships || [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Walkthrough
  const [walkthroughSeen, setWalkthroughSeen] = useLocalStorage('budgetCenter.walkthroughSeen', false);
  const [welcomeDismissed, setWelcomeDismissed] = useLocalStorage('budgetCenter.welcomeDismissed', false);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const walkthroughRefs = {
    clientSelector: useRef(null),
    monthNavigator: useRef(null),
    workflowStepper: useRef(null),
    budgetTable: useRef(null),
    actionBar: useRef(null),
    discussion: useRef(null),
  };
  const [selectedClient, setSelectedClient] = useState(searchParams.get('client_id') || '');
  const [selectedClientOption, setSelectedClientOption] = useState(null);
  const [period, setPeriod] = useState(searchParams.get('period') || getCurrentMonthStart());
  const [pageData, setPageData] = useState(null);
  const [segments, setSegments] = useState([emptySegment(period)]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);
  const [rowErrors, setRowErrors] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [adjustmentsDialogOpen, setAdjustmentsDialogOpen] = useState(false);
  const [adjustmentError, setAdjustmentError] = useState('');
  const [adjustmentMode, setAdjustmentMode] = useState('delta');
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [turnOffDialogSegment, setTurnOffDialogSegment] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [markLiveDialogOpen, setMarkLiveDialogOpen] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState(null);
  const [zeroAmountWarningOpen, setZeroAmountWarningOpen] = useState(false);
  const [copyMonthDialogOpen, setCopyMonthDialogOpen] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [copySourcePeriod, setCopySourcePeriod] = useState('');
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);
  const [submitSuccessOpen, setSubmitSuccessOpen] = useState(false);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null);
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  // Spend lookup: { "vertId|locId" => { month_to_date_spend, projected_spend, ... } }
  const [spendMap, setSpendMap] = useState({});
  // Multi-client: search and collapse state
  const [multiClientSearch, setMultiClientSearch] = useState('');
  const [collapsedClients, setCollapsedClients] = useState({});
  // Multi-client dialog context: when set, dialogs operate on this client's data
  const [mcDialogClientId, setMcDialogClientId] = useState(null);

  const isStaff = Boolean(user?.is_staff || user?.is_superuser);
  const directMembershipClients = useMemo(
    () => memberships.filter((membership) => membership?.client_id),
    [memberships]
  );
  const isParentScopeMember = useMemo(
    () => !isStaff && memberships.length > 0 && !memberships[0]?.client_id && Boolean(
      memberships[0]?.parent_company_id || memberships[0]?.parent_company_name
    ),
    [isStaff, memberships]
  );
  const parentCompanyId = isParentScopeMember ? memberships[0]?.parent_company_id : undefined;
  const parentCompanyName = isParentScopeMember ? memberships[0]?.parent_company_name : '';
  const isMultiClientMode = isParentScopeMember && !isStaff;

  const {
    options: clientOptions,
    loading: clientLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete({ parentCompanyId });

  // Multi-client hook for parent scope members
  const multiClient = useMultiClientBudgetCenter({
    clientOptions,
    period,
    enabled: isMultiClientMode,
  });

  // Feature flag: parent scope members always see it (flag checked per-client on load);
  // direct members need at least one client with the flag enabled
  const clientBudgetCenterEnabled = isStaff || isParentScopeMember || directMembershipClients.some((m) => m.budget_center_enabled);
  const accessibleClientCount = isStaff
    ? clientOptions.length
    : directMembershipClients.length > 0
      ? directMembershipClients.length
      : clientOptions.length;
  const canShowClientSelector = isStaff;
  const singleClientMembership = !isStaff && !isParentScopeMember && directMembershipClients.length === 1
    ? directMembershipClients[0]
    : null;
  const readOnlyClientName = pageData?.client?.name
    || selectedClientOption?.label
    || singleClientMembership?.client_name
    || '';

  useEffect(() => {
    if (selectedClient) {
      const option = findOptionByValue(selectedClient);
      if (option) {
        setSelectedClientOption(option);
      }
      api.request({
        url: `dashboard/client-budgets/budget-verticals/?client_id=${selectedClient}`,
        method: 'GET',
      })
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
          setVerticalOptions(rows.map((row) => ({ value: row.id, label: row.vertical })));
        })
        .catch(() => setVerticalOptions([]));
      api.request({
        url: 'dashboard/client-territories/',
        method: 'GET',
        payload: { client_id: selectedClient },
      })
        .then((response) => {
          const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
          setLocationOptions(rows.map((row) => ({ value: row.id, label: row.name })));
        })
        .catch(() => setLocationOptions([]));
      return;
    }

    setVerticalOptions([]);
    setLocationOptions([]);

    const fallbackClientId = searchParams.get('client_id')
      || memberships.find((membership) => membership.client_id)?.client_id
      || (clientOptions.length === 1 ? String(clientOptions[0].value) : '');
    if (fallbackClientId) {
      setSelectedClient(String(fallbackClientId));
    }
  }, [clientOptions, findOptionByValue, memberships, searchParams, selectedClient]);

  // When staff preview mode changes, auto-select the previewed client
  useEffect(() => {
    if (isPreviewMode && viewAsClient?.id) {
      setSelectedClient(String(viewAsClient.id));
    }
  }, [isPreviewMode, viewAsClient?.id]);

  const loadBudgetCenter = useCallback(() => {
    if (!selectedClient || isMultiClientMode) {
      return;
    }
    setLoading(true);
    setPageData(null);
    setBanner(null);
    setSelectedSegmentIndex(null);
    setComments([]);
    setCommentText('');
    setAdjustmentsDialogOpen(false);
    setNotesDialogOpen(false);
    setHistoryOpen(false);
    Promise.all([
      fetchBudgetCenter({ client_id: selectedClient, period }),
      fetchBudgetCenterScopeSpend({
        client_id: selectedClient,
        period,
      }).catch(() => ({ data: [] })),
    ])
      .then(([bcResponse, spendResponse]) => {
        const payload = bcResponse.data;
        setPageData(payload);
        setSegments(
          Array.isArray(payload?.segments) && payload.segments.length
            ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
            : [emptySegment(payload?.period || period)]
        );
        setRowErrors([]);

        // Build spend lookup map keyed by "verticalId|locationId"
        const map = {};
        for (const row of (spendResponse.data || [])) {
          const key = `${row.vertical_id || ''}|${row.location_id || ''}`;
          map[key] = {
            month_to_date_spend: row.month_to_date_spend || 0,
          };
        }
        setSpendMap(map);
      })
      .catch((error) => {
        setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to load Budget Center.') });
      })
      .finally(() => setLoading(false));
  }, [period, selectedClient]);

  useEffect(() => {
    loadBudgetCenter();
  }, [loadBudgetCenter]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (isMultiClientMode) {
      nextParams.delete('client_id');
    } else if (selectedClient) {
      nextParams.set('client_id', String(selectedClient));
    } else {
      nextParams.delete('client_id');
    }
    if (period) {
      nextParams.set('period', period);
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [isMultiClientMode, period, searchParams, selectedClient, setSearchParams]);

  // Auto-start walkthrough for first-time visitors once data is loaded
  // Skip walkthrough in multi-client mode — the layout is different
  useEffect(() => {
    if (isMultiClientMode) return;
    if (!walkthroughSeen && !loading && pageData && selectedClient && !walkthroughActive) {
      const timer = setTimeout(() => setWalkthroughActive(true), 600);
      return () => clearTimeout(timer);
    }
  }, [isMultiClientMode, walkthroughSeen, loading, pageData, selectedClient, walkthroughActive]);

  const walkthroughSteps = isStaff ? WALKTHROUGH_STEPS_STAFF : WALKTHROUGH_STEPS_CLIENT;

  const endWalkthrough = useCallback(() => {
    setWalkthroughActive(false);
    setWalkthroughStep(0);
    setWalkthroughSeen(true);
  }, [setWalkthroughSeen]);

  const nextWalkthroughStep = useCallback(() => {
    if (walkthroughStep >= walkthroughSteps.length - 1) {
      endWalkthrough();
    } else {
      setWalkthroughStep((prev) => prev + 1);
    }
  }, [endWalkthrough, walkthroughStep, walkthroughSteps.length]);

  const prevWalkthroughStep = useCallback(() => {
    setWalkthroughStep((prev) => Math.max(0, prev - 1));
  }, []);

  const currentSummary = pageData?.current_budget || null;
  const previousSummary = pageData?.previous_budget || null;
  const isCurrentMonth = dayjs(period).isSame(dayjs(), 'month');
  const isFutureMonth = dayjs(period).isAfter(dayjs(), 'month');
  const isPastMonth = dayjs(period).isBefore(dayjs(), 'month');
  const isReadOnly = isPastMonth && !isStaff;
  const hasLiveBudgetRows = Array.isArray(pageData?.live_budget_ids)
    && pageData.live_budget_ids.some((id) => {
      const matchingRow = (pageData?.segments || []).find((segment) => Number(segment.budget_id) === Number(id));
      return String(matchingRow?.workflow_status || '').toLowerCase() === 'active';
    });
  const isLiveMonth = isCurrentMonth && (
    hasLiveBudgetRows || String(pageData?.status || '').toLowerCase() === 'active'
  );
  const isApprovedPendingLive = String(pageData?.status || '').toLowerCase() === 'approved';
  const isPendingApproval = String(pageData?.status || '').toLowerCase() === 'pending_approval';
  const showAdjustmentColumns = isLiveMonth;
  const allSegmentsOff = showAdjustmentColumns
    && segments.length > 0
    && segments.every((segment) => String(getSegmentOperationalStatus(segment)).toLowerCase() === 'off');
  const optionSet = useMemo(
    () => ({ verticals: verticalOptions, locations: locationOptions }),
    [verticalOptions, locationOptions]
  );
  const selectedSegment = selectedSegmentIndex === null ? null : segments[selectedSegmentIndex] || null;

  // Multi-client dialog helpers: when mcDialogClientId is set, derive segment/options from multi-client data
  const mcClientData = mcDialogClientId ? multiClient.clientDataMap[mcDialogClientId] : null;
  const dialogSegment = mcDialogClientId
    ? (selectedSegmentIndex !== null && mcClientData ? mcClientData.segments[selectedSegmentIndex] || null : null)
    : selectedSegment;
  const dialogOptionSet = mcDialogClientId && mcClientData
    ? { verticals: mcClientData.verticalOptions || [], locations: mcClientData.locationOptions || [] }
    : optionSet;

  const approvalTotal = useMemo(
    () => segments.reduce((total, segment) => total + Number(segment.amount || 0), 0),
    [segments]
  );
  const effectiveTotal = useMemo(
    () => segments.reduce((total, segment) => total + Number(segment.effective_amount || segment.amount || 0), 0),
    [segments]
  );
  const previousAmount = Number(previousSummary?.effective_amount || previousSummary?.amount || 0);
  const currentAmountForDelta = showAdjustmentColumns ? effectiveTotal : approvalTotal;
  const monthDelta = previousAmount ? currentAmountForDelta - previousAmount : null;
  const monthDeltaPct = previousAmount ? ((monthDelta / previousAmount) * 100) : null;

  // Per-segment previous-month lookup for delta hints
  const previousSegments = pageData?.previous_segments || [];
  const getPreviousSegmentAmount = useCallback((segment) => {
    if (!previousSegments.length) return null;
    const match = previousSegments.find(
      (prev) =>
        String(prev.vertical_id || '') === String(segment.vertical || '')
        && String(prev.location_id || '') === String(segment.location || '')
        && String(prev.channel_id || '') === String(segment.channel || '')
    );
    return match ? Number(match.amount) : null;
  }, [previousSegments]);

  // Check if segments are completely unchanged from what was loaded (seed or prior save)
  const segmentsUnchangedFromLoad = useMemo(() => {
    const loaded = Array.isArray(pageData?.segments) ? pageData.segments : [];
    if (!loaded.length || loaded.length !== segments.length) return false;
    return segments.every((seg, i) => {
      const orig = loaded[i];
      return String(seg.amount || 0) === String(orig.amount || orig.starting_budget || 0)
        && String(seg.vertical || '') === String(orig.vertical || orig.vertical_id || '')
        && String(seg.location || '') === String(orig.location || orig.location_id || '');
    });
  }, [pageData?.segments, segments]);

  // Progress checklist state
  const progressSteps = useMemo(() => {
    const status = String(pageData?.status || '').toLowerCase();
    const hasAmounts = segments.some((s) => Number(s.amount || 0) > 0);
    return {
      reviewed: hasAmounts,
      saved: pageData?.has_draft || status !== 'draft',
      submitted: ['pending_approval', 'approved', 'active'].includes(status),
    };
  }, [pageData?.status, pageData?.has_draft, segments]);
  const hasDraftChanges = useMemo(() => {
    if (pageData?.has_draft) {
      return true;
    }
    const loadedSegments = Array.isArray(pageData?.segments) ? pageData.segments : [];
    if (loadedSegments.length !== segments.length) {
      return true;
    }
    const currentComparable = JSON.stringify(segments.map(segmentDraftComparable));
    const loadedComparable = JSON.stringify(
      loadedSegments.map((segment) => segmentDraftComparable(normalizeSegment(segment, pageData?.period || period)))
    );
    return currentComparable !== loadedComparable;
  }, [pageData, period, segments]);
  const approvalActionLabel = isStaff
    ? (isPendingApproval
      ? 'Approve Budget'
      : isLiveMonth ? 'Approve Changes' : 'Approve Budget')
    : (isLiveMonth ? 'Submit Changes' : 'Submit for Review');
  const saveDraftLabel = isStaff ? 'Save Draft' : 'Save Progress';

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handler = (event) => {
      if (!hasDraftChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasDraftChanges]);

  const buildPayload = useCallback(() => {
    // For live months, only send segments that are new or actually changed.
    // Unchanged live-origin segments stay untouched — no draft cloning needed.
    const loadedSegments = Array.isArray(pageData?.segments) ? pageData.segments : [];
    const segmentsToSubmit = isLiveMonth
      ? segments.filter((segment) => {
        // New segment (no budget_id) — always include
        if (!segment.budget_id) return true;
        // Already a draft — always include
        if (pageData?.draft_budget_ids?.includes(segment.budget_id)) return true;
        // Live-origin: check if anything changed vs loaded version
        const loaded = loadedSegments.find(
          (s) => (s.budget_id || s.id) === segment.budget_id
        );
        if (!loaded) return true;
        // Compare key fields
        return (
          String(segment.amount || '') !== String(loaded.amount || '')
          || String(segment.vertical || '') !== String(loaded.vertical || loaded.vertical_data?.id || '')
          || String(segment.location || '') !== String(loaded.location || loaded.location_data?.id || '')
          || String(segment.notes || '') !== String(loaded.notes || '')
        );
      })
      : segments;

    return {
      client_id: Number(selectedClient),
      period,
      allocation_mode: 'optimize_freely',
      allocation_config: {},
      notes: '',
      segments: segmentsToSubmit.map((segment) => ({
        budget_id: segment.budget_id || null,
        source_budget_id: segment.source_budget_id || null,
        vertical: segment.vertical || null,
        vertical_ids: segment.vertical ? [Number(segment.vertical)] : [],
        location: segment.location || null,
        location_ids: (segment.location_ids || []).length
          ? segment.location_ids.map(Number)
          : (segment.location ? [Number(segment.location)] : []),
        channel: segment.channel || null,
        amount: segment.amount,
        notes: segment.notes || '',
      })),
    };
  }, [isLiveMonth, pageData?.live_budget_ids, period, segments, selectedClient]);

  const validateSegments = useCallback(() => {
    const nextRowErrors = segments.map(() => ({}));
    const seenKeys = new Map();

    segments.forEach((segment, index) => {
      if (segment.amount === '' || segment.amount === null || segment.amount === undefined) {
        nextRowErrors[index].amount = 'Starting budget is required.';
      } else if (Number.isNaN(Number(segment.amount))) {
        nextRowErrors[index].amount = 'Enter a valid number.';
      } else if (Number(segment.amount) < 0) {
        nextRowErrors[index].amount = 'Starting budget cannot be negative.';
      }

      const key = `${segment.vertical || 'all'}|${segment.location || 'all'}|${segment.channel || 'all'}`;
      if (seenKeys.has(key)) {
        nextRowErrors[index].segment = 'This segment already exists in the table.';
        const firstIndex = seenKeys.get(key);
        nextRowErrors[firstIndex].segment = 'This segment already exists in the table.';
      } else {
        seenKeys.set(key, index);
      }
    });

    setRowErrors(nextRowErrors);
    return nextRowErrors.every((row) => !Object.keys(row).length);
  }, [segments]);

  const runBudgetAction = useCallback(async (requestFn, successMessage) => {
    if (!selectedClient) {
      setBanner({ severity: 'error', message: 'Select a client first.' });
      return null;
    }
    if (!validateSegments()) {
      setBanner({ severity: 'error', message: 'Fix the highlighted rows before continuing.' });
      return null;
    }
    const payload = buildPayload();
    if (!payload.segments.length) {
      setBanner({ severity: 'info', message: 'No changes to submit.' });
      return null;
    }
    setSaving(true);
    try {
      const response = await requestFn(payload);
      const responseData = response.data;
      setPageData(responseData);
      setSegments(
        Array.isArray(responseData?.segments) && responseData.segments.length
          ? responseData.segments.map((segment) => normalizeSegment(segment, responseData.period || period))
          : [emptySegment(period)]
      );
      setRowErrors([]);
      setBanner(null);
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
      return responseData;
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Budget action failed.') });
      return null;
    } finally {
      setSaving(false);
    }
  }, [buildPayload, period, selectedClient]);

  const saveDraft = useCallback(
    () => runBudgetAction(saveBudgetCenterDraft, 'Draft saved.'),
    [runBudgetAction]
  );
  const approveDraft = useCallback(
    async () => {
      const result = await runBudgetAction(
        approveBudgetCenter,
        isStaff
          ? 'Budget approved. Mark it live when ready.'
          : 'Budget submitted for review. Lead Gurus will review and make it live.'
      );
      if (result && !isStaff) {
        setSubmitSuccessOpen(true);
      }
      return result;
    },
    [isStaff, runBudgetAction]
  );
  const markLive = useCallback(
    async () => {
      if (!selectedClient) {
        setBanner({ severity: 'error', message: 'Select a client first.' });
        return;
      }
      setSaving(true);
      try {
        const response = await markBudgetCenterLive({
          client_id: Number(selectedClient),
          period,
        });
        const payload = response.data;
        setPageData(payload);
        setSegments(
          Array.isArray(payload?.segments) && payload.segments.length
            ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
            : [emptySegment(period)]
        );
        setBanner(null);
        setSnackbar({ open: true, message: 'Budget marked live.', severity: 'success' });
      } catch (error) {
        setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to mark budget live.') });
      } finally {
        setSaving(false);
      }
    },
    [period, selectedClient]
  );

  const discardDraft = useCallback(async () => {
    if (!selectedClient) {
      return;
    }
    setSaving(true);
    try {
      const response = await discardBudgetCenterDraft({
        client_id: Number(selectedClient),
        period,
      });
      const payload = response.data;
      setPageData(payload);
      setSegments(
        Array.isArray(payload?.segments) && payload.segments.length
          ? payload.segments.map((segment) => normalizeSegment(segment, payload.period || period))
          : [emptySegment(period)]
      );
      setBanner(null);
      setSnackbar({ open: true, message: 'Changes discarded.', severity: 'success' });
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to discard draft.') });
    } finally {
      setSaving(false);
    }
  }, [period, selectedClient]);

  const updateSegment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => {
      if (rowIndex !== index) return segment;
      const updated = { ...segment, [field]: value };
      // When vertical or location changes, look up MTD spend from the scope-spend map
      if (field === 'vertical' || field === 'location') {
        const vertId = field === 'vertical' ? value : segment.vertical;
        const locId = field === 'location' ? value : segment.location;
        // Try exact combo first, then vertical-only, then overall
        const exactKey = `${vertId || ''}|${locId || ''}`;
        const vertOnlyKey = `${vertId || ''}|`;
        const overallKey = '|';
        const spend = spendMap[exactKey] || (locId ? spendMap[vertOnlyKey] : null) || spendMap[overallKey];
        updated.month_to_date_spend = spend?.month_to_date_spend || 0;
      }
      return updated;
    }));
    setRowErrors((prev) => prev.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const next = { ...(row || {}) };
      if (field === 'amount') delete next.amount;
      delete next.segment;
      return next;
    }));
  };

  const updatePendingAdjustment = (index, field, value) => {
    setSegments((prev) => prev.map((segment, rowIndex) => (
      rowIndex === index
        ? {
          ...segment,
          pendingAdjustment: {
            ...(segment.pendingAdjustment || {}),
            [field]: value,
          },
        }
        : segment
    )));
  };

  const duplicateSegment = useCallback((index) => {
    setSegments((prev) => {
      const source = prev[index];
      if (!source) {
        return prev;
      }
      const copy = cloneSegment(source);
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
    setRowErrors((prev) => {
      const next = Array.isArray(prev) ? [...prev] : [];
      next.splice(index + 1, 0, {});
      return next;
    });
  }, []);

  const addAdjustment = useCallback(async () => {
    const seg = mcDialogClientId ? dialogSegment : selectedSegment;
    if (selectedSegmentIndex === null || !seg) {
      return;
    }
    const pendingAdjustment = seg.pendingAdjustment || {};
    if (!pendingAdjustment.amount_delta || !pendingAdjustment.effective_date) {
      setAdjustmentError('Adjustment amount and effective date are required.');
      return;
    }

    const budgetId = seg.budget_id;
    if (!budgetId) {
      setAdjustmentError('Save the budget first before adding adjustments.');
      return;
    }

    setAdjustmentError('');
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${budgetId}/adjustments/`,
        method: 'POST',
        payload: pendingAdjustment,
      });
      setAdjustmentError('');
      setSnackbar({ open: true, message: 'Adjustment added.', severity: 'success' });
      setAdjustmentsDialogOpen(false);
      setSelectedSegmentIndex(null);
      if (mcDialogClientId) {
        setMcDialogClientId(null);
        multiClient.reload();
      } else {
        loadBudgetCenter();
      }
    } catch (error) {
      setAdjustmentError(getErrorMessage(error, 'Failed to add adjustment.'));
    } finally {
      setSaving(false);
    }
  }, [dialogSegment, loadBudgetCenter, mcDialogClientId, multiClient, selectedSegment, selectedSegmentIndex]);

  const deleteAdjustment = useCallback(async (adjustmentId) => {
    const seg = mcDialogClientId ? dialogSegment : selectedSegment;
    const mcClientPageData = mcDialogClientId ? multiClient.clientDataMap[mcDialogClientId]?.pageData : null;
    const effectivePageData = mcDialogClientId ? mcClientPageData : pageData;
    if (!seg?.budget_id || !effectivePageData?.draft_budget_ids?.includes(seg.budget_id)) {
      setBanner({ severity: 'info', message: 'Save a draft before removing adjustments.' });
      return;
    }
    setSaving(true);
    try {
      await api.request({
        url: `dashboard/client-budgets/${seg.budget_id}/adjustments/${adjustmentId}/`,
        method: 'DELETE',
      });
      setBanner(null);
      setSnackbar({ open: true, message: 'Adjustment removed.', severity: 'success' });
      if (mcDialogClientId) {
        multiClient.reload();
      } else {
        loadBudgetCenter();
      }
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to remove adjustment.') });
    } finally {
      setSaving(false);
    }
  }, [dialogSegment, loadBudgetCenter, mcDialogClientId, multiClient, pageData, selectedSegment]);

  const turnOffSegment = useCallback(async (segment) => {
    if (!segment) {
      return;
    }
    const effectiveAmount = Number(segment.effective_amount || segment.amount || 0);
    const monthToDateSpend = Number(segment.month_to_date_spend || 0);
    const amountDelta = monthToDateSpend - effectiveAmount;

    if (amountDelta >= 0) {
      setBanner({
        severity: 'info',
        message: `This segment is already at its lowest allowed effective budget of ${formatCurrency(monthToDateSpend)}.`,
      });
      return;
    }

    const budgetId = segment.budget_id;
    if (!budgetId) {
      setBanner({ severity: 'error', message: 'Save the budget first before turning off segments.' });
      return;
    }

    const clientIdForTurnOff = mcDialogClientId || selectedClient;
    setSaving(true);
    try {
      if (isStaff) {
        // Staff can turn off directly
        await api.request({
          url: `dashboard/client-budgets/${budgetId}/adjustments/`,
          method: 'POST',
          payload: {
            amount_delta: amountDelta.toFixed(2),
            effective_date: dayjs().format('YYYY-MM-DD'),
            notes: 'Turn off segment',
          },
        });
        setBanner(null);
        setSnackbar({ open: true, message: 'Segment turned off.', severity: 'success' });
      } else {
        // Clients submit a turn-off request for staff review
        await api.request({
          url: 'dashboard/client-budgets/budget-center/request-turn-off/',
          method: 'POST',
          payload: {
            client_id: Number(clientIdForTurnOff),
            period,
            budget_id: budgetId,
          },
        });
        setBanner(null);
        setSnackbar({ open: true, message: 'Turn-off request submitted for staff review.', severity: 'success' });
      }
      if (mcDialogClientId) {
        multiClient.reload();
      } else {
        loadBudgetCenter();
      }
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to turn off segment.') });
    } finally {
      setSaving(false);
    }
  }, [isStaff, loadBudgetCenter, mcDialogClientId, multiClient, period, selectedClient]);

  const openTurnOffDialog = useCallback((segment) => {
    setTurnOffDialogSegment(segment || null);
  }, []);

  const closeTurnOffDialog = useCallback(() => {
    setTurnOffDialogSegment(null);
  }, []);

  const confirmTurnOffSegment = useCallback(async () => {
    if (!turnOffDialogSegment) {
      return;
    }
    await turnOffSegment(turnOffDialogSegment);
    setTurnOffDialogSegment(null);
    setMcDialogClientId(null);
  }, [turnOffDialogSegment, turnOffSegment]);

  const openNotesDialog = useCallback((index) => {
    setSelectedSegmentIndex(index);
    setNotesDialogOpen(true);
  }, []);

  const closeNotesDialog = useCallback(() => {
    setNotesDialogOpen(false);
  }, []);

  const openApproveDialog = useCallback(() => {
    if (!validateSegments()) {
      setBanner({ severity: 'error', message: 'Fix the highlighted rows before continuing.' });
      return;
    }
    const hasZeroSegments = segments.some((s) => Number(s.amount) === 0);
    if (hasZeroSegments) {
      setZeroAmountWarningOpen(true);
      return;
    }
    setApproveDialogOpen(true);
  }, [segments, validateSegments]);

  const closeApproveDialog = useCallback(() => {
    setApproveDialogOpen(false);
  }, []);

  const confirmApproveDraft = useCallback(async () => {
    setApproveDialogOpen(false);
    await approveDraft();
  }, [approveDraft]);

  const confirmMarkLive = useCallback(async () => {
    setMarkLiveDialogOpen(false);
    await markLive();
  }, [markLive]);

  const confirmDiscard = useCallback(async () => {
    setDiscardDialogOpen(false);
    await discardDraft();
  }, [discardDraft]);

  const confirmDeleteRow = useCallback(() => {
    if (deleteRowIndex === null) return;
    setSegments((prev) => prev.filter((_, rowIndex) => rowIndex !== deleteRowIndex));
    setRowErrors((prev) => prev.filter((_, rowIndex) => rowIndex !== deleteRowIndex));
    setDeleteRowIndex(null);
  }, [deleteRowIndex]);

  const openCopyMonthDialog = useCallback(async () => {
    if (!selectedClient) return;
    try {
      const response = await fetchAvailableMonths({ client_id: selectedClient });
      setAvailableMonths(response.data || []);
      setCopySourcePeriod('');
      setCopyMonthDialogOpen(true);
    } catch {
      setBanner({ severity: 'error', message: 'Failed to load available months.' });
    }
  }, [selectedClient]);

  const confirmCopyFromMonth = useCallback(async () => {
    if (!selectedClient || !copySourcePeriod) return;
    setCopyMonthDialogOpen(false);
    setLoading(true);
    try {
      const response = await copyFromMonth({
        client_id: selectedClient,
        target_period: period,
        source_period: copySourcePeriod,
      });
      const data = response.data;
      if (Array.isArray(data?.segments) && data.segments.length) {
        setSegments(data.segments.map((s) => normalizeSegment(s, period)));
        setSnackbar({ open: true, message: `Copied ${data.segments.length} segment(s) from ${copySourcePeriod}.`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'No segments found for the selected month.', severity: 'info' });
      }
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to copy segments.') });
    } finally {
      setLoading(false);
    }
  }, [selectedClient, copySourcePeriod, period]);

  const loadComments = useCallback(async () => {
    if (!selectedClient || !period) return;
    try {
      const response = await fetchBudgetComments({ client_id: selectedClient, period });
      setComments(response.data || []);
    } catch {
      setComments([]);
    }
  }, [selectedClient, period]);

  const submitComment = useCallback(async () => {
    if (!commentText.trim() || !selectedClient) return;
    setCommentSaving(true);
    try {
      await postBudgetComment({ client_id: selectedClient, period, body: commentText.trim() });
      setCommentText('');
      await loadComments();
    } catch (error) {
      setBanner({ severity: 'error', message: getErrorMessage(error, 'Failed to post comment.') });
    } finally {
      setCommentSaving(false);
    }
  }, [commentText, selectedClient, period, loadComments]);

  // Load comments when client/period changes
  useEffect(() => {
    if (selectedClient && period) {
      loadComments();
    } else {
      setComments([]);
    }
  }, [selectedClient, period, loadComments]);

  if (!clientBudgetCenterEnabled) {
    return (
      <Page title="Budget Center" fullWidth>
        <Stack spacing={3} alignItems="center" sx={{ py: 10 }}>
          <Typography variant="h5">Budget Center</Typography>
          <Typography color="text.secondary" variant="body1" textAlign="center">
            Budget Center is not yet enabled for your account. Contact Lead Gurus if you&apos;d like to start managing your budgets here.
          </Typography>
        </Stack>
      </Page>
    );
  }

  return (
    <Page title="Budget Center" fullWidth>
      <Stack spacing={2}>
        {banner ? (
          <Alert onClose={() => setBanner(null)} severity={banner.severity}>
            {banner.message}
          </Alert>
        ) : null}

        {/* Staff: keep the full client selector */}
        {canShowClientSelector ? (
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Grid container spacing={2} alignItems="center">
                <Grid ref={walkthroughRefs.clientSelector} item xs={12} md={5}>
                  <Autocomplete
                    loading={clientLoading}
                    onChange={(_, option) => {
                      setSelectedClient(option?.value ? String(option.value) : '');
                      setSelectedClientOption(option || null);
                    }}
                    onInputChange={handleClientSearch}
                    options={clientOptions}
                    value={selectedClientOption}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                    renderOption={(props, option) => (
                      <li {...props} key={option?.value ?? option?.label}>
                        {option?.label || ''}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Client" placeholder="Select client" />}
                  />
                </Grid>
                <Grid ref={walkthroughRefs.monthNavigator} item xs={12} md={3}>
                  <MonthNavigator isStaff={isStaff} onChange={(newPeriod) => setPeriod(newPeriod)} value={period} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Chip
                    color={getStatusChipColor(pageData?.status)}
                    label={formatStatusLabel(pageData?.status, { planned: isFutureMonth && String(pageData?.status || '').toLowerCase() === 'draft' })}
                    variant="filled"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ) : null}

        {/* Multi-client mode: show all clients in one view */}
        {isMultiClientMode ? (
          <>
            {/* Month header — mirrors the single-client layout */}
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
                      {parentCompanyName ? (
                        <Typography variant="subtitle1" fontWeight={700}>
                          {parentCompanyName}
                        </Typography>
                      ) : null}
                      <MonthNavigator isStaff={isStaff} onChange={(newPeriod) => setPeriod(newPeriod)} value={period} />
                      <Tooltip title="Refresh data">
                        <IconButton size="small" onClick={() => multiClient.reload()} disabled={multiClient.loading}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  {/* Inline summary bar */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 3 }}
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                  >
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Budget</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{formatCurrency(multiClient.aggregates.totalBudget)}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Segments</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{multiClient.aggregates.totalSegments}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Clients</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{multiClient.aggregates.clientCount}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {Object.entries(multiClient.aggregates.statusCounts)
                        .map(([status, count]) => `${count} ${formatStatusLabel(status)}`)
                        .join(', ') || 'No data'}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {multiClient.loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : multiClient.error ? (
              <Alert severity="error">{multiClient.error}</Alert>
            ) : (
              <>
                {/* Multi-client grouped table */}
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5}>
                        <Box>
                          <Typography variant="h6">Monthly Budget Table</Typography>
                          <Typography color="text.secondary" variant="body2">
                            All clients for {formatMonthLabel(period)}. Each client has its own workflow.
                          </Typography>
                        </Box>
                        <TextField
                          size="small"
                          placeholder="Search clients..."
                          value={multiClientSearch}
                          onChange={(e) => setMultiClientSearch(e.target.value)}
                          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                          sx={{ minWidth: 220 }}
                        />
                      </Stack>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'auto', maxHeight: '70vh' }}>
                        <Table>
                          <TableHead>
                            <TableRow
                              sx={{
                                bgcolor: 'var(--mui-palette-background-level1)',
                                '& th': { position: 'sticky', top: 0, zIndex: 2, bgcolor: 'var(--mui-palette-background-level1)' },
                              }}
                            >
                              <TableCell>Client</TableCell>
                              <TableCell>Vertical</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell>Starting Budget</TableCell>
                              <TableCell>Adjustments</TableCell>
                              <TableCell>Effective Budget</TableCell>
                              <TableCell>MTD Spend</TableCell>
                              <TableCell>Remaining</TableCell>
                              <TableCell>Remaining/Day</TableCell>
                              <TableCell>Pacing</TableCell>
                              <TableCell>Projected</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {multiClient.clientEntries
                              .filter(([, clientData]) =>
                                !multiClientSearch.trim()
                                || (clientData.clientName || '').toLowerCase().includes(multiClientSearch.trim().toLowerCase())
                              )
                              .map(([clientId, clientData]) => {
                              const clientStatus = String(clientData.pageData?.status || 'draft').toLowerCase();
                              const clientTotal = clientData.segments.reduce(
                                (sum, s) => sum + Number(s.amount || 0), 0
                              );
                              const effectiveTotal = clientData.segments.reduce(
                                (sum, s) => sum + Number(s.effective_amount || s.amount || 0), 0
                              );
                              const isPendingApprovalClient = clientStatus === 'pending_approval';
                              const isApprovedClient = clientStatus === 'approved';
                              const isActiveClient = clientStatus === 'active';
                              const isCurrentMonthClient = dayjs(period).isSame(dayjs(), 'month');
                              const clientHasLiveRows = Array.isArray(clientData.pageData?.live_budget_ids)
                                && clientData.pageData.live_budget_ids.some((id) => {
                                  const matchingRow = (clientData.pageData?.segments || []).find((s) => Number(s.budget_id) === Number(id));
                                  return String(matchingRow?.workflow_status || '').toLowerCase() === 'active';
                                });
                              const isClientLiveMonth = isCurrentMonthClient && (clientHasLiveRows || isActiveClient);
                              return (
                                <React.Fragment key={clientId}>
                                  {/* Client group header */}
                                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell colSpan={13}>
                                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                          <Typography fontWeight={700} variant="subtitle2">
                                            {clientData.clientName}
                                          </Typography>
                                          <Chip
                                            color={getStatusChipColor(clientStatus)}
                                            label={formatStatusLabel(clientStatus)}
                                            size="small"
                                            variant="filled"
                                          />
                                          <Typography variant="caption" color="text.secondary">
                                            {clientData.segments.length} segment{clientData.segments.length !== 1 ? 's' : ''}
                                            {' '}&middot; {formatCurrency(clientTotal)} budget
                                            {effectiveTotal !== clientTotal ? ` &middot; ${formatCurrency(effectiveTotal)} effective` : ''}
                                          </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                          {isStaff && isApprovedClient ? (
                                            <Button
                                              size="small"
                                              color="success"
                                              variant="contained"
                                              disabled={clientData.saving}
                                              onClick={() => multiClient.markLiveForClient(clientId).catch(() => {})}
                                            >
                                              Mark Live
                                            </Button>
                                          ) : null}
                                          {!isActiveClient && !isPendingApprovalClient ? (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              disabled={clientData.saving}
                                              onClick={() => multiClient.approveForClient(clientId).catch(() => {})}
                                            >
                                              {isPendingApprovalClient ? 'Approve' : 'Submit'}
                                            </Button>
                                          ) : null}
                                          {!isActiveClient ? (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              disabled={clientData.saving}
                                              onClick={() => multiClient.saveDraftForClient(clientId).catch(() => {})}
                                            >
                                              Save
                                            </Button>
                                          ) : null}
                                          {clientData.saving ? <CircularProgress size={20} /> : null}
                                        </Stack>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                  {clientData.error ? (
                                    <TableRow>
                                      <TableCell colSpan={13}>
                                        <Alert severity="error" onClose={() => multiClient.clearErrorForClient(clientId)}>
                                          {clientData.error}
                                        </Alert>
                                      </TableCell>
                                    </TableRow>
                                  ) : null}
                                  {/* Segment rows for this client */}
                                  {clientData.segments.map((segment, index) => {
                                    const segRemaining = Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0);
                                    return (
                                    <TableRow key={`${clientId}-${segment.budget_id || index}`} hover sx={{ '& td': { py: 1.5 }, ...(segRemaining < 0 ? { bgcolor: 'rgba(211, 47, 47, 0.06)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.12) !important' } } : {}) }}>
                                      <TableCell>
                                        {index === 0 ? (
                                          <Typography variant="body2" color="text.secondary">
                                            {clientData.clientName}
                                          </Typography>
                                        ) : null}
                                      </TableCell>
                                      <TableCell>
                                        <TextField
                                          disabled={isActiveClient}
                                          select
                                          size="small"
                                          fullWidth
                                          value={segment.vertical}
                                          onChange={(e) => multiClient.updateSegmentForClient(clientId, index, 'vertical', e.target.value)}
                                        >
                                          <MenuItem value="">All</MenuItem>
                                          {(clientData.verticalOptions || []).map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                          ))}
                                        </TextField>
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 200 }}>
                                        <Autocomplete
                                          multiple
                                          size="small"
                                          disabled={isActiveClient}
                                          options={clientData.locationOptions || []}
                                          getOptionLabel={(opt) => opt.label || ''}
                                          value={(clientData.locationOptions || []).filter((o) => (segment.location_ids || (segment.location ? [Number(segment.location)] : [])).includes(o.value))}
                                          onChange={(_, val) => {
                                            const ids = (val || []).map((item) => item.value);
                                            multiClient.updateSegmentForClient(clientId, index, 'location_ids', ids);
                                            multiClient.updateSegmentForClient(clientId, index, 'location', ids[0] || '');
                                          }}
                                          isOptionEqualToValue={(option, value) => option.value === value.value}
                                          renderInput={(params) => <TextField {...params} placeholder={segment.location_ids?.length ? '' : 'All'} />}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ minWidth: 140 }}>
                                        <TextField
                                          disabled={isActiveClient}
                                          size="small"
                                          fullWidth
                                          inputProps={{ inputMode: 'decimal' }}
                                          InputProps={{
                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                          }}
                                          value={formatNumberInput(segment.amount)}
                                          onChange={(e) => multiClient.updateSegmentForClient(clientId, index, 'amount', e.target.value.replace(/,/g, ''))}
                                        />
                                      </TableCell>
                                      <TableCell>{formatCurrency(segment.adjustment_total)}</TableCell>
                                      <TableCell>{formatCurrency(segment.effective_amount || segment.amount)}</TableCell>
                                      <TableCell>{formatCurrency(segment.month_to_date_spend)}</TableCell>
                                      {(() => {
                                        const remaining = Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0);
                                        return (
                                          <TableCell sx={remaining < 0 ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                            {formatCurrency(remaining)}
                                          </TableCell>
                                        );
                                      })()}
                                      <TableCell sx={segRemaining < 0 ? { color: 'error.main', fontWeight: 600 } : undefined}>{formatCurrency(segment.remaining_per_day)}</TableCell>
                                      <TableCell>
                                        <PacingCell effectiveAmount={segment.effective_amount || segment.amount} monthToDateSpend={segment.month_to_date_spend} pacingPercentage={segment.pacing_percentage} />
                                      </TableCell>
                                      <TableCell>{formatCurrency(segment.projected_spend)}</TableCell>
                                      <TableCell>
                                        <Chip
                                          color={getStatusChipColor(segment.workflow_status || clientStatus)}
                                          label={formatStatusLabel(segment.workflow_status || clientStatus)}
                                          size="small"
                                          variant="filled"
                                        />
                                      </TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setMcDialogClientId(clientId);
                                            setSelectedSegmentIndex(index);
                                            setNotesDialogOpen(true);
                                          }}
                                        >
                                          {segment.notes ? 'Edit Notes' : 'Notes'}
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => multiClient.duplicateSegmentForClient(clientId, index)}
                                        >
                                          Duplicate
                                        </Button>
                                        {isClientLiveMonth ? (
                                          <Button
                                            color="primary"
                                            size="small"
                                            startIcon={<EditOutlinedIcon />}
                                            sx={{ mx: 0.5 }}
                                            variant="contained"
                                            onClick={() => {
                                              setMcDialogClientId(clientId);
                                              setSelectedSegmentIndex(index);
                                              setAdjustmentsDialogOpen(true);
                                            }}
                                          >
                                            Add Adjustment
                                          </Button>
                                        ) : null}
                                        {isClientLiveMonth ? (
                                          <Button
                                            size="small"
                                            onClick={() => {
                                              setMcDialogClientId(clientId);
                                              setTurnOffDialogSegment(segment);
                                            }}
                                          >
                                            Turn Off
                                          </Button>
                                        ) : null}
                                        <Tooltip title={clientData.segments.length === 1 ? 'At least one segment is required' : (isClientLiveMonth && isLiveOriginSegment(segment, clientData.pageData?.live_budget_ids)) ? 'Live segments cannot be deleted' : 'Remove segment'}>
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => multiClient.deleteSegmentForClient(clientId, index)}
                                              disabled={(isClientLiveMonth && isLiveOriginSegment(segment, clientData.pageData?.live_budget_ids)) || clientData.segments.length === 1}
                                            >
                                              <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        ) : null}

        {/* Single-client mode loading/empty states */}
        {!isMultiClientMode && loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : null}

        {!isMultiClientMode && !loading && !selectedClient ? (
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                <Typography color="text.secondary" variant="h6">
                  Select a client to get started
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Choose a client above to view and manage their monthly budget.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {!isMultiClientMode && !loading && selectedClient ? (
          <>
            <Card ref={walkthroughRefs.budgetTable} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  {/* ── Compact header: client · month · status ── */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1.5}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
                      {!isStaff ? (
                        <Typography variant="subtitle1" fontWeight={700}>
                          {readOnlyClientName}
                        </Typography>
                      ) : null}
                      <Box ref={walkthroughRefs.monthNavigator}>
                        <MonthNavigator isStaff={isStaff} onChange={(newPeriod) => setPeriod(newPeriod)} value={period} />
                      </Box>
                      <Chip
                        color={getStatusChipColor(pageData?.status)}
                        label={formatStatusLabel(pageData?.status, { planned: isFutureMonth && String(pageData?.status || '').toLowerCase() === 'draft' })}
                        size="small"
                        variant="filled"
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title="Refresh data">
                        <IconButton size="small" onClick={loadBudgetCenter} disabled={loading}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        startIcon={<HelpOutlineIcon />}
                        variant="text"
                        onClick={() => { setWalkthroughStep(0); setWalkthroughActive(true); }}
                      >
                        Tour
                      </Button>
                      {isPastMonth && !isStaff ? null : (
                        <Tooltip
                          title={!isStaff ? 'Most clients perform best with one flexible budget. Exact segment targets cannot be guaranteed due to the dynamic nature of Meta advertising.' : ''}
                          placement="bottom"
                        >
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            variant="outlined"
                            color={!isStaff && segments.length >= 1 ? 'inherit' : 'primary'}
                            onClick={() => setSegments((prev) => [...prev, emptySegment(period)])}
                          >
                            Add Segment
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>

                  {/* ── Inline summary bar ── */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 3 }}
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
                    sx={{ px: 0.5, flexWrap: 'wrap' }}
                  >
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Budget</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{formatCurrency(currentSummary?.amount)}</Typography>
                      {monthDelta !== null ? (
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{ color: monthDelta > 0 ? 'success.main' : monthDelta < 0 ? 'error.main' : 'text.secondary' }}
                        >
                          {monthDelta > 0 ? '+' : ''}{formatCurrency(monthDelta)}
                          {monthDeltaPct !== null ? ` (${monthDeltaPct > 0 ? '+' : ''}${monthDeltaPct.toFixed(0)}%)` : ''}
                        </Typography>
                      ) : null}
                    </Stack>
                    {showAdjustmentColumns ? (
                      <Stack direction="row" alignItems="baseline" spacing={0.75}>
                        <Typography variant="subtitle2" color="text.secondary">Effective</Typography>
                        <Typography variant="subtitle2" fontWeight={700}>{formatCurrency(currentSummary?.effective_amount || currentSummary?.amount)}</Typography>
                      </Stack>
                    ) : null}
                    <Stack direction="row" alignItems="baseline" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary">Segments</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>{currentSummary?.segment_count || segments.length || 0}</Typography>
                    </Stack>
                    {previousAmount ? (
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        {pageData?.seeded_from_previous
                          ? `Copied from ${formatMonthLabel(pageData?.seed_source_period)}`
                          : `vs ${formatCurrency(previousAmount)} last month`}
                      </Typography>
                    ) : null}
                  </Stack>

                  <Divider />

                  {/* Optimization guidance — discourage unnecessary segments */}
                  {!isStaff && !isLiveMonth ? (
                    <Box
                      sx={{
                        bgcolor: segments.length > 1 ? 'warning.50' : 'action.hover',
                        border: segments.length > 1 ? '1px solid' : 'none',
                        borderColor: 'warning.200',
                        borderRadius: 1.5,
                        px: 2,
                        py: 1.25,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {segments.length > 1 ? (
                          <>
                            <strong>Tip:</strong> You have {segments.length} segments.
                            Fewer segments give Lead Gurus more flexibility to allocate your spend
                            where it performs best, lowering your overall cost of marketing. Most clients see the
                            best results with a single flexible budget.
                            Due to the dynamic nature of Meta advertising, exact budget segment targets are best-effort
                            and cannot be guaranteed.
                          </>
                        ) : (
                          <>
                            <strong>Recommended:</strong> One flexible budget lets Lead Gurus use real-time
                            data to optimize your campaigns and lower your cost of marketing. Only add
                            segments if you need hard caps by vertical or market.
                          </>
                        )}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Welcome banner for new clients with no budget history */}
                  {!isStaff && !pageData?.seeded_from_previous && !pageData?.previous_budget
                    && (!Array.isArray(pageData?.segments) || pageData.segments.length === 0) ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Welcome to Budget Center</Typography>
                      <Typography variant="body2">
                        Set your monthly ad-spend budget below. Most clients perform best with a single flexible budget —
                        just enter your total amount and submit. Lead Gurus will review and activate it before the month begins.
                      </Typography>
                    </Alert>
                  ) : null}

                  {/* Quick submit shortcut for unchanged seeded budgets */}
                  {!isStaff && !isLiveMonth && segmentsUnchangedFromLoad
                    && approvalTotal > 0 && !progressSteps.submitted && pageData?.seeded_from_previous ? (
                    <Alert
                      severity="success"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          disabled={saving}
                          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                          onClick={openApproveDialog}
                        >
                          Looks Good — Submit
                        </Button>
                      }
                    >
                      Same budget as {formatMonthLabel(pageData?.seed_source_period)}. Submit as-is or adjust below.
                    </Alert>
                  ) : null}

                  {isMobile ? (
                    <Stack spacing={1.5}>
                      {segments.map((segment, index) => {
                        const rowStatus = getBudgetCenterRowStatus(segment, {
                          isLiveMonth,
                          liveBudgetIds: pageData?.live_budget_ids,
                        });
                        return (
                          <MobileSegmentCard
                            key={`${segment.budget_id || 'new'}-${index}`}
                            segment={segment}
                            index={index}
                            rowErrors={rowErrors[index]}
                            isLiveMonth={isLiveMonth}
                            liveOrigin={isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)}
                            showAdjustmentColumns={showAdjustmentColumns}
                            verticalOptions={verticalOptions}
                            locationOptions={locationOptions}
                            updateSegment={updateSegment}
                            onNotes={openNotesDialog}
                            onDuplicate={duplicateSegment}
                            onAdjustment={(i) => { setSelectedSegmentIndex(i); setAdjustmentsDialogOpen(true); }}
                            onTurnOff={openTurnOffDialog}
                            onDelete={setDeleteRowIndex}
                            canDelete={segments.length > 1}
                            rowStatus={rowStatus}
                            isFutureMonth={isFutureMonth}
                            optionSet={optionSet}
                            previousAmount={getPreviousSegmentAmount(segment)}
                            isReadOnly={isReadOnly}
                          />
                        );
                      })}
                    </Stack>
                  ) : (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ borderRadius: 2, overflow: 'auto', maxHeight: '65vh' }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow
                            sx={{
                              bgcolor: 'var(--mui-palette-background-level1)',
                              '& th': {
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                bgcolor: 'var(--mui-palette-background-level1)',
                              },
                            }}
                          >
                            <TableCell>Vertical</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell>Starting Budget</TableCell>
                            {showAdjustmentColumns ? <TableCell>Adjustments</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Effective Budget</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>MTD Spend</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Remaining</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Remaining/Day</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Pacing</TableCell> : null}
                            {showAdjustmentColumns ? <TableCell>Projected</TableCell> : null}
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {segments.map((segment, index) => {
                            const rowStatus = getBudgetCenterRowStatus(segment, {
                              isLiveMonth,
                              liveBudgetIds: pageData?.live_budget_ids,
                            });
                            const singleRemaining = Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0);
                            const isOverspent = showAdjustmentColumns && singleRemaining < 0;
                            return (
                            <TableRow
                              key={`${segment.budget_id || 'new'}-${index}`}
                              hover
                              sx={{
                                '& td': {
                                  py: 1.75,
                                  verticalAlign: 'top',
                                },
                                ...(isOverspent ? { bgcolor: 'rgba(211, 47, 47, 0.06)', '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.12) !important' } } : {}),
                              }}
                            >
                              <TableCell sx={{ minWidth: 180 }}>
                                <Stack spacing={0.75}>
                                  <TextField
                                    disabled={isReadOnly || (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids))}
                                    error={Boolean(rowErrors[index]?.segment)}
                                    fullWidth
                                    select
                                    size="small"
                                    value={segment.vertical}
                                    onChange={(event) => updateSegment(index, 'vertical', event.target.value)}
                                  >
                                    <MenuItem value="">All</MenuItem>
                                    {verticalOptions.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  {!segment.vertical && !segment.location ? (
                                    <Chip label="Flexible" size="small" sx={{ alignSelf: 'flex-start' }} variant="soft" />
                                  ) : null}
                                  {rowErrors[index]?.segment ? (
                                    <Typography color="error" variant="caption">
                                      {rowErrors[index].segment}
                                    </Typography>
                                  ) : null}
                                </Stack>
                              </TableCell>
                              <TableCell sx={{ minWidth: 200 }}>
                                <Autocomplete
                                  multiple
                                  size="small"
                                  disabled={isReadOnly || (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids))}
                                  options={locationOptions}
                                  getOptionLabel={(opt) => opt.label || ''}
                                  value={locationOptions.filter((o) => (segment.location_ids || (segment.location ? [Number(segment.location)] : [])).includes(o.value))}
                                  onChange={(_, val) => {
                                    const ids = (val || []).map((item) => item.value);
                                    updateSegment(index, 'location_ids', ids);
                                    updateSegment(index, 'location', ids[0] || '');
                                  }}
                                  isOptionEqualToValue={(option, value) => option.value === value.value}
                                  renderInput={(params) => <TextField {...params} placeholder={segment.location_ids?.length ? '' : 'All'} />}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 140 }}>
                                <TextField
                                  disabled={isReadOnly || (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids))}
                                  error={Boolean(rowErrors[index]?.amount)}
                                  fullWidth
                                  helperText={rowErrors[index]?.amount || ''}
                                  size="small"
                                  inputProps={{ inputMode: 'decimal' }}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  }}
                                  value={formatNumberInput(segment.amount)}
                                  onChange={(event) => updateSegment(index, 'amount', event.target.value.replace(/,/g, ''))}
                                />
                                {(() => {
                                  const prevAmt = getPreviousSegmentAmount(segment);
                                  if (prevAmt === null || prevAmt === 0) return null;
                                  const delta = Number(segment.amount || 0) - prevAmt;
                                  if (delta === 0) return (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                      Same as last month
                                    </Typography>
                                  );
                                  return (
                                    <Typography
                                      variant="caption"
                                      sx={{ mt: 0.5, color: delta > 0 ? 'success.main' : 'error.main' }}
                                    >
                                      was {formatCurrency(prevAmt)} last month
                                    </Typography>
                                  );
                                })()}
                              </TableCell>
                              {showAdjustmentColumns ? (
                                <TableCell>{formatCurrency(segment.adjustment_total)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>{formatCurrency(segment.effective_amount || segment.amount)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>{formatCurrency(segment.month_to_date_spend)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (() => {
                                const remaining = Number(segment.effective_amount || segment.amount || 0) - Number(segment.month_to_date_spend || 0);
                                return (
                                  <TableCell sx={remaining < 0 ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                    {formatCurrency(remaining)}
                                  </TableCell>
                                );
                              })() : null}
                              {showAdjustmentColumns ? (
                                <TableCell sx={isOverspent ? { color: 'error.main', fontWeight: 600 } : undefined}>{formatCurrency(segment.remaining_per_day)}</TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>
                                  <PacingCell effectiveAmount={segment.effective_amount || segment.amount} monthToDateSpend={segment.month_to_date_spend} pacingPercentage={segment.pacing_percentage} />
                                </TableCell>
                              ) : null}
                              {showAdjustmentColumns ? (
                                <TableCell>{formatCurrency(segment.projected_spend)}</TableCell>
                              ) : null}
                              <TableCell>
                                <Chip
                                  color={getStatusChipColor(rowStatus)}
                                  label={formatStatusLabel(rowStatus, {
                                    planned: isFutureMonth && String(rowStatus || '').toLowerCase() === 'draft',
                                  })}
                                  size="small"
                                  variant="filled"
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                <Button
                                  size="small"
                                  onClick={() => openNotesDialog(index)}
                                >
                                  {segment.notes ? 'Edit Notes' : 'Notes'}
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => duplicateSegment(index)}
                                >
                                  Duplicate
                                </Button>
                                {showAdjustmentColumns ? (
                                  <Button
                                    color="primary"
                                    size="small"
                                    startIcon={<EditOutlinedIcon />}
                                    sx={{ mx: 0.5 }}
                                    variant="contained"
                                    onClick={() => {
                                      setSelectedSegmentIndex(index);
                                      setAdjustmentsDialogOpen(true);
                                    }}
                                  >
                                    Add Adjustment
                                  </Button>
                                ) : null}
                                {isLiveMonth ? (
                                  <Button
                                    size="small"
                                    onClick={() => openTurnOffDialog(segment)}
                                  >
                                    Turn Off
                                  </Button>
                                ) : null}
                                <Tooltip title={segments.length === 1 ? 'At least one segment is required' : (isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)) ? 'Live segments cannot be deleted' : 'Remove segment'}>
                                  <span>
                                    <IconButton
                                      onClick={() => setDeleteRowIndex(index)}
                                      disabled={(isLiveMonth && isLiveOriginSegment(segment, pageData?.live_budget_ids)) || segments.length === 1}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card
              ref={walkthroughRefs.actionBar}
              variant="outlined"
              sx={{
                borderRadius: 3,
                position: 'sticky',
                bottom: 16,
                zIndex: 5,
                overflow: 'visible',
                boxShadow: 3,
              }}
            >
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                  <Stack spacing={0.5}>
                    <Stack alignItems={{ xs: 'flex-start', sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Typography variant="h6">
                        {formatCurrency(showAdjustmentColumns && effectiveTotal !== approvalTotal ? effectiveTotal : approvalTotal)}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {showAdjustmentColumns && effectiveTotal !== approvalTotal
                          ? `effective · ${formatCurrency(approvalTotal)} starting`
                          : 'total budget'}
                      </Typography>
                    </Stack>
                    <Typography color="text.secondary" variant="body2">
                      {allSegmentsOff
                        ? 'All segments are off. Add new segments or increase existing ones with adjustments.'
                        : isLiveMonth && hasDraftChanges
                          ? (isStaff ? 'Review pending changes and approve when ready.' : 'You have pending changes. Submit when ready.')
                          : isLiveMonth
                            ? 'Starting budgets are locked. Use Add Adjustment on any row to make in-month changes.'
                            : isApprovedPendingLive
                              ? (isStaff ? 'This budget is approved and ready to go live.' : 'Your budget is approved and will be made live by Lead Gurus.')
                              : isPendingApproval
                                ? (isStaff ? 'Client submitted this budget. Review and approve, then mark it live.' : 'Your budget has been submitted and is under review.')
                                : isStaff
                                  ? 'Set starting budgets and approve when ready.'
                                  : 'Set your budget amounts, then submit for review.'}
                    </Typography>
                    {pageData?.activity_summary?.saved_draft ? (
                      <Typography color="text.secondary" variant="caption">
                        Saved by {formatActorLabel(pageData.activity_summary.saved_draft)} · {formatDateTime(pageData.activity_summary.saved_draft.created_at)}
                      </Typography>
                    ) : null}
                    {pageData?.activity_summary?.submitted ? (
                      <Typography color="text.secondary" variant="caption">
                        Submitted by {formatActorLabel(pageData.activity_summary.submitted)} · {formatDateTime(pageData.activity_summary.submitted.created_at)}
                      </Typography>
                    ) : null}
                    {pageData?.activity_summary?.approved ? (
                      <Typography color="text.secondary" variant="caption">
                        Approved by {formatActorLabel(pageData.activity_summary.approved)} · {formatDateTime(pageData.activity_summary.approved.created_at)}
                      </Typography>
                    ) : null}
                    {pageData?.activity_summary?.marked_live ? (
                      <Typography color="text.secondary" variant="caption">
                        Made live by {formatActorLabel(pageData.activity_summary.marked_live)} · {formatDateTime(pageData.activity_summary.marked_live.created_at)}
                      </Typography>
                    ) : null}
                    {comments.length > 0 ? (
                      <Chip
                        icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                        label={`${comments.length} comment${comments.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={() => walkthroughRefs.discussion.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        sx={{ alignSelf: 'flex-start', cursor: 'pointer' }}
                      />
                    ) : null}
                  </Stack>

                  {isPastMonth && !isStaff ? (
                    <Chip label="View Only — Past Month" variant="outlined" />
                  ) : (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ alignItems: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
                  >
                    {/* Staff: Mark Live — primary action when budget is approved */}
                    {isStaff && isApprovedPendingLive ? (
                      <Button
                        color="success"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
                        variant="contained"
                        onClick={() => setMarkLiveDialogOpen(true)}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
                      >
                        Mark Live
                      </Button>
                    ) : null}

                    {/* Client: submitted state — no action buttons, show status chip */}
                    {!isStaff && (isApprovedPendingLive || isPendingApproval) && !hasDraftChanges ? (
                      <Chip
                        color={isApprovedPendingLive ? 'success' : 'info'}
                        label={isApprovedPendingLive ? 'Approved — going live soon' : 'Submitted — under review'}
                        variant="outlined"
                      />
                    ) : null}

                    {/* Primary submit/approve button — shown when there are actions to take */}
                    {(!isApprovedPendingLive || hasDraftChanges) && (!isPendingApproval || hasDraftChanges || isStaff) && (!isLiveMonth || hasDraftChanges) ? (
                      <Button
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
                        variant="contained"
                        onClick={openApproveDialog}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 160 }}
                      >
                        {approvalActionLabel}
                      </Button>
                    ) : null}

                    {/* Secondary save button */}
                    {(!isApprovedPendingLive || hasDraftChanges) && (!isPendingApproval || hasDraftChanges || isStaff) && (!isLiveMonth || hasDraftChanges) ? (
                      <Button
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                        variant="outlined"
                        onClick={saveDraft}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 140 }}
                      >
                        {saveDraftLabel}
                      </Button>
                    ) : null}

                    {/* Discard — only when there's a draft to discard */}
                    {pageData?.has_draft ? (
                      <Tooltip title="Revert to the last saved version">
                        <span>
                          <Button
                            color="error"
                            disabled={saving}
                            startIcon={<UndoOutlinedIcon />}
                            variant="text"
                            onClick={() => setDiscardDialogOpen(true)}
                            sx={{ width: { xs: '100%', sm: 'auto' } }}
                          >
                            Discard Changes
                          </Button>
                        </span>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box>
                      <Typography variant="h6">Change History</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Recent budget changes for this client and month.
                      </Typography>
                    </Box>
                    <Button
                      disabled={!(pageData?.history || []).length}
                      onClick={() => setHistoryOpen((prev) => !prev)}
                      variant="text"
                    >
                      {historyOpen ? 'Hide History' : 'Show History'}
                    </Button>
                  </Stack>

                  {historyOpen ? (
                    (pageData?.history || []).length ? (
                      <Stack spacing={1.5}>
                        {pageData.history.map((entry) => (
                          <Box
                            key={entry.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 2,
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Box>
                                  <Typography variant="subtitle2">
                                    {entry.summary}
                                  </Typography>
                                  <Typography color="text.secondary" variant="caption">
                                    {(entry.actor?.name || entry.actor?.email || 'System')} • {formatDateTime(entry.created_at)}
                                  </Typography>
                                </Box>
                                {entry.entity_label ? (
                                  <Chip label={entry.entity_label} size="small" variant="outlined" />
                                ) : null}
                              </Stack>

                              {entry.changed_fields?.length ? (
                                <Typography color="text.secondary" variant="body2">
                                  Changed: {entry.changed_fields.join(', ')}
                                </Typography>
                              ) : null}

                              {entry.changed_fields?.length && (entry.before || entry.after) ? (
                                <Stack spacing={0.75}>
                                  {entry.changed_fields.map((field) => {
                                    const beforeValue = entry.before?.[field];
                                    const afterValue = entry.after?.[field];
                                    return (
                                      <Typography key={`${entry.id}-${field}`} variant="body2">
                                        <Box component="span" sx={{ fontWeight: 600 }}>
                                          {field}
                                        </Box>
                                        : {formatHistoryValue(beforeValue)} {' -> '} {formatHistoryValue(afterValue)}
                                      </Typography>
                                    );
                                  })}
                                </Stack>
                              ) : null}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">No budget change history yet for this month.</Alert>
                    )
                  ) : null}
                </Stack>
              </CardContent>
            </Card>

            {/* Adjustments and Notes dialogs moved outside !isMultiClientMode block */}

            <Dialog fullWidth maxWidth="xs" onClose={() => setZeroAmountWarningOpen(false)} open={zeroAmountWarningOpen}>
              <DialogTitle>$0 Segments Detected</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Alert severity="warning">
                    {segments.filter((s) => Number(s.amount) === 0).length} segment{segments.filter((s) => Number(s.amount) === 0).length > 1 ? 's have' : ' has'} a $0 budget and will not receive any traffic.
                  </Alert>
                  <Typography color="text.secondary" variant="body2">
                    You can continue or go back to adjust the amounts.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setZeroAmountWarningOpen(false)}>Go Back</Button>
                <Button
                  variant="contained"
                  onClick={() => { setZeroAmountWarningOpen(false); setApproveDialogOpen(true); }}
                >
                  Continue Anyway
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog fullWidth maxWidth="sm" onClose={closeApproveDialog} open={approveDialogOpen}>
              <DialogTitle>{approvalActionLabel}</DialogTitle>
              <DialogContent>
                {(() => {
                  const submittingPayload = buildPayload();
                  const submittingSegments = submittingPayload.segments || [];
                  const submittingTotal = submittingSegments.reduce((sum, s) => sum + Number(s.amount || 0), 0);
                  const getLabel = (id, options) => {
                    if (!id) return 'All';
                    const match = options.find((o) => String(o.value) === String(id));
                    return match?.label || String(id);
                  };
                  return (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                      <Typography color="text.secondary" variant="body2">
                        {formatMonthLabel(period)} · {readOnlyClientName || 'this client'}
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ '& th': { fontWeight: 600, py: 0.75 } }}>
                            <TableCell>Vertical</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {submittingSegments.map((seg, i) => (
                            <TableRow key={i} sx={{ '& td': { py: 0.75 } }}>
                              <TableCell>{getLabel(seg.vertical, verticalOptions)}</TableCell>
                              <TableCell>{getLabel(seg.location, locationOptions)}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={600}>{formatCurrency(seg.amount)}</Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ '& td': { borderBottom: 'none', pt: 1.5 } }}>
                            <TableCell colSpan={2}>
                              <Typography fontWeight={700}>
                                {isLiveMonth ? 'Changes Total' : 'Total'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={700}>{formatCurrency(submittingTotal)}</Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Alert severity="info">
                        {isStaff
                          ? (isLiveMonth
                            ? 'These changes will be approved. You will still need to mark them live.'
                            : 'This will approve the budget. You will still need to mark it live.')
                          : (isLiveMonth
                            ? 'Your changes will be submitted for review by Lead Gurus before going live.'
                            : 'Your budget will be submitted for review by Lead Gurus before going live.')}
                      </Alert>
                    </Stack>
                  );
                })()}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeApproveDialog}>Cancel</Button>
                <Button
                  onClick={confirmApproveDraft}
                  startIcon={<CheckCircleOutlineIcon />}
                  variant="contained"
                >
                  {isStaff
                    ? (isLiveMonth ? 'Confirm Approval' : 'Confirm Approval')
                    : (isLiveMonth ? 'Submit Changes' : 'Submit for Review')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Turn Off dialog moved outside !isMultiClientMode block */}

            {/* Mark Live confirmation */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setMarkLiveDialogOpen(false)} open={markLiveDialogOpen}>
              <DialogTitle>Mark Budget Live</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    This will activate the {formatMonthLabel(period)} budget for {readOnlyClientName || 'this client'}. The client will be notified.
                  </Typography>
                  <Alert severity="warning">
                    This action cannot be undone. The budget will immediately become active.
                  </Alert>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setMarkLiveDialogOpen(false)}>Cancel</Button>
                <Button color="success" onClick={confirmMarkLive} variant="contained">
                  Confirm — Mark Live
                </Button>
              </DialogActions>
            </Dialog>

            {/* Discard confirmation */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setDiscardDialogOpen(false)} open={discardDialogOpen}>
              <DialogTitle>Discard Changes</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    This will revert to the last saved version. Any unsaved edits will be lost.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDiscardDialogOpen(false)}>Cancel</Button>
                <Button color="error" onClick={confirmDiscard} variant="contained">
                  Discard
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete row confirmation */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setDeleteRowIndex(null)} open={deleteRowIndex !== null}>
              <DialogTitle>Remove Segment</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    Remove this segment from the table? This only takes effect when you save or submit.
                  </Typography>
                  {deleteRowIndex !== null && segments[deleteRowIndex] ? (
                    <Chip
                      label={buildSegmentLabel(segments[deleteRowIndex], optionSet)}
                      variant="outlined"
                    />
                  ) : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteRowIndex(null)}>Cancel</Button>
                <Button color="error" onClick={confirmDeleteRow} variant="contained">
                  Remove
                </Button>
              </DialogActions>
            </Dialog>

            {/* Copy from month dialog */}
            <Dialog fullWidth maxWidth="xs" onClose={() => setCopyMonthDialogOpen(false)} open={copyMonthDialogOpen}>
              <DialogTitle>Copy Budget From Another Month</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Typography color="text.secondary" variant="body2">
                    Select a month to copy its budget segments into {formatMonthLabel(period)}.
                  </Typography>
                  <TextField
                    fullWidth
                    label="Source Month"
                    select
                    value={copySourcePeriod}
                    onChange={(e) => setCopySourcePeriod(e.target.value)}
                  >
                    {availableMonths.map((m) => (
                      <MenuItem key={m.period} value={m.period}>{m.label}</MenuItem>
                    ))}
                  </TextField>
                  {!availableMonths.length ? (
                    <Alert severity="info">No months with live or approved budgets found for this client.</Alert>
                  ) : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setCopyMonthDialogOpen(false)}>Cancel</Button>
                <Button disabled={!copySourcePeriod} onClick={confirmCopyFromMonth} variant="contained">
                  Copy Segments
                </Button>
              </DialogActions>
            </Dialog>

            {/* Budget Discussion */}
            <Card ref={walkthroughRefs.discussion} variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">Budget Discussion</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Discuss budget changes with {isStaff ? 'the client' : 'Lead Gurus'} for this month.
                      </Typography>
                    </Box>
                    {comments.length ? (
                      <Chip label={`${comments.length} comment${comments.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                    ) : null}
                  </Stack>

                  {comments.length ? (
                    <Stack spacing={1.5} sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {comments.map((comment) => (
                        <Box
                          key={comment.id}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                        >
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2">
                                {comment.author?.name || comment.author?.email || 'Unknown'}
                              </Typography>
                              <Typography color="text.secondary" variant="caption">
                                {formatDateTime(comment.created_at)}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {comment.body}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No comments yet. Start the conversation below.
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Add a comment..."
                      size="small"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                    />
                    <Button
                      disabled={!commentText.trim() || commentSaving}
                      variant="contained"
                      onClick={submitComment}
                      sx={{ minWidth: 100, height: 56 }}
                    >
                      {commentSaving ? <CircularProgress size={20} /> : 'Post'}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </>
        ) : null}
      </Stack>

      {/* Shared dialogs — rendered outside single/multi client conditionals */}
      <Dialog fullWidth maxWidth="md" onClose={() => { setAdjustmentsDialogOpen(false); setAdjustmentError(''); setMcDialogClientId(null); setAdjustmentMode('delta'); }} open={adjustmentsDialogOpen}>
        <DialogTitle>Segment Adjustments</DialogTitle>
        <DialogContent>
          {dialogSegment ? (() => {
            const startingAmt = Number(dialogSegment.amount || 0);
            const adjTotal = Number(dialogSegment.adjustment_total || 0);
            const currentEffective = Number(dialogSegment.effective_amount || dialogSegment.amount || 0);
            const pendingDeltaRaw = dialogSegment.pendingAdjustment?.amount_delta;
            const pendingDelta = Number(pendingDeltaRaw || 0);
            const hasPendingDelta = pendingDeltaRaw !== '' && pendingDeltaRaw !== null && pendingDeltaRaw !== undefined && !Number.isNaN(pendingDelta);
            const newEffective = currentEffective + (hasPendingDelta ? pendingDelta : 0);
            const newTotalFieldValue = hasPendingDelta
              ? String(Number((currentEffective + pendingDelta).toFixed(2)))
              : '';
            const setPendingDelta = (value) => {
              if (mcDialogClientId) {
                multiClient.updatePendingAdjustmentForClient(mcDialogClientId, selectedSegmentIndex, 'amount_delta', value);
              } else {
                updatePendingAdjustment(selectedSegmentIndex, 'amount_delta', value);
              }
            };
            const handleNewTotalChange = (value) => {
              if (value === '' || value === '-') {
                setPendingDelta('');
                return;
              }
              const parsed = Number(value);
              if (Number.isNaN(parsed)) {
                setPendingDelta('');
                return;
              }
              const nextDelta = Number((parsed - currentEffective).toFixed(2));
              setPendingDelta(String(nextDelta));
            };
            return (
              <Stack spacing={2} sx={{ pt: 1 }}>
                {adjustmentError ? (
                  <Alert severity="error" onClose={() => setAdjustmentError('')}>
                    {adjustmentError}
                  </Alert>
                ) : null}

                <Typography variant="subtitle2">
                  {buildSegmentLabel(dialogSegment, dialogOptionSet)}
                </Typography>

                <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}>
                  <Typography color="text.secondary" variant="caption">
                    Current effective budget
                  </Typography>
                  <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }} variant="h5">
                    {formatCurrency(currentEffective)}
                  </Typography>
                  <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                    Starting {formatCurrency(startingAmt)}
                    {adjTotal !== 0 ? (
                      <>
                        {' · Adjustments '}
                        {adjTotal > 0 ? '+' : ''}
                        {formatCurrency(adjTotal)}
                      </>
                    ) : null}
                  </Typography>
                </Box>

                {(dialogSegment.adjustments || []).length ? (
                  <Stack spacing={1}>
                    <Typography color="text.secondary" variant="caption">
                      Existing adjustments
                    </Typography>
                    {(dialogSegment.adjustments || []).map((adjustment) => (
                      <Box key={adjustment.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box>
                            <Typography variant="body1">
                              {Number(adjustment.amount_delta) > 0 ? '+' : ''}
                              {formatCurrency(adjustment.amount_delta)}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              Effective {dayjs(adjustment.effective_date).format('MMM D, YYYY')}
                            </Typography>
                            {adjustment.notes ? (
                              <Typography color="text.secondary" variant="body2">
                                {adjustment.notes}
                              </Typography>
                            ) : null}
                          </Box>
                          <Button variant="text" onClick={() => deleteAdjustment(adjustment.id)}>
                            Remove
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Add an adjustment
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Pick how you want to enter the change — we&apos;ll store the difference, not a replacement amount.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => setAdjustmentMode('delta')}
                    size="small"
                    variant={adjustmentMode === 'delta' ? 'contained' : 'outlined'}
                  >
                    Change by amount (+/−)
                  </Button>
                  <Button
                    onClick={() => setAdjustmentMode('total')}
                    size="small"
                    variant={adjustmentMode === 'total' ? 'contained' : 'outlined'}
                  >
                    Set new total
                  </Button>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    {adjustmentMode === 'delta' ? (
                      <TextField
                        fullWidth
                        helperText="Enter a positive number to add, or a negative (−) to subtract."
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        label="Change by"
                        placeholder="e.g. 500 or -500"
                        type="number"
                        value={pendingDeltaRaw || ''}
                        onChange={(event) => setPendingDelta(event.target.value)}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        helperText={`Current is ${formatCurrency(currentEffective)}. We'll compute the change for you.`}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        label="New effective total"
                        type="number"
                        value={newTotalFieldValue}
                        onChange={(event) => handleNewTotalChange(event.target.value)}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Effective Date"
                      type="date"
                      value={dialogSegment.pendingAdjustment?.effective_date || getTodayDateString()}
                      onChange={(event) => {
                        if (mcDialogClientId) {
                          multiClient.updatePendingAdjustmentForClient(mcDialogClientId, selectedSegmentIndex, 'effective_date', event.target.value);
                        } else {
                          updatePendingAdjustment(selectedSegmentIndex, 'effective_date', event.target.value);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={dialogSegment.pendingAdjustment?.notes || ''}
                      onChange={(event) => {
                        if (mcDialogClientId) {
                          multiClient.updatePendingAdjustmentForClient(mcDialogClientId, selectedSegmentIndex, 'notes', event.target.value);
                        } else {
                          updatePendingAdjustment(selectedSegmentIndex, 'notes', event.target.value);
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {hasPendingDelta && pendingDelta !== 0 ? (
                  <Alert icon={false} severity={pendingDelta >= 0 ? 'success' : 'warning'}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          New effective budget
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }} variant="h6">
                          {formatCurrency(newEffective)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          Change
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }} variant="h6">
                          {pendingDelta > 0 ? '+' : ''}
                          {formatCurrency(pendingDelta)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography color="text.secondary" variant="caption">
                          From current
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {formatCurrency(currentEffective)} → {formatCurrency(newEffective)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Alert>
                ) : null}
              </Stack>
            );
          })() : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAdjustmentsDialogOpen(false); setMcDialogClientId(null); setAdjustmentMode('delta'); }}>Close</Button>
          <Button onClick={addAdjustment} variant="contained">
            Save Adjustment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={() => { closeNotesDialog(); setMcDialogClientId(null); }} open={notesDialogOpen}>
        <DialogTitle>Segment Notes</DialogTitle>
        <DialogContent>
          {dialogSegment ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="subtitle2">
                {buildSegmentLabel(dialogSegment, dialogOptionSet)}
              </Typography>
              <TextField
                fullWidth
                minRows={4}
                multiline
                value={dialogSegment.notes || ''}
                onChange={(event) => {
                  if (mcDialogClientId) {
                    multiClient.updateSegmentForClient(mcDialogClientId, selectedSegmentIndex, 'notes', event.target.value);
                  } else {
                    updateSegment(selectedSegmentIndex, 'notes', event.target.value);
                  }
                }}
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { closeNotesDialog(); setMcDialogClientId(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" onClose={() => { closeTurnOffDialog(); setMcDialogClientId(null); }} open={Boolean(turnOffDialogSegment)}>
        <DialogTitle>Confirm Turn Off Segment</DialogTitle>
        <DialogContent>
          {turnOffDialogSegment ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">
                Turning off a live segment creates a negative adjustment. It cannot reduce the effective budget below what has already been spent.
              </Alert>
              <Typography variant="subtitle2">
                {buildSegmentLabel(turnOffDialogSegment, dialogOptionSet)}
              </Typography>
              <Box>
                <Typography variant="body2">
                  Current effective budget: {formatCurrency(turnOffDialogSegment.effective_amount || turnOffDialogSegment.amount)}
                </Typography>
                <Typography variant="body2">
                  Month-to-date spend: {formatCurrency(turnOffDialogSegment.month_to_date_spend)}
                </Typography>
                <Typography variant="body2">
                  Lowest allowed effective budget after turn off: {formatCurrency(turnOffDialogSegment.month_to_date_spend)}
                </Typography>
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { closeTurnOffDialog(); setMcDialogClientId(null); }}>Cancel</Button>
          <Button color="warning" onClick={confirmTurnOffSegment} variant="contained">
            Confirm Turn Off
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-submit celebration dialog */}
      <Dialog
        open={submitSuccessOpen}
        onClose={() => setSubmitSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, textAlign: 'center', py: 2 } }}
      >
        <DialogContent>
          <Stack spacing={2.5} alignItems="center" sx={{ py: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>
            <Typography variant="h6">Budget Submitted!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
              Your {formatMonthLabel(period)} budget has been submitted for review.
              Lead Gurus will review it and mark it live before the month starts.
            </Typography>
            <Divider flexItem />
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                What happens next:
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  1. Lead Gurus reviews your segments and amounts
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  2. You&apos;ll get an email when it&apos;s approved and live
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                  3. Adjust anytime during the month if your needs change
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button variant="contained" onClick={() => setSubmitSuccessOpen(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        open={snackbar.open}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {walkthroughActive ? (
        <WalkthroughOverlay
          stepIndex={walkthroughStep}
          steps={walkthroughSteps}
          stepRefs={walkthroughRefs}
          onNext={nextWalkthroughStep}
          onBack={prevWalkthroughStep}
          onSkip={endWalkthrough}
        />
      ) : null}
    </Page>
  );
}
